import type { AgentToolDefinition } from '@multiplayer-app/ai-agent-react'
import type { OfficeHost } from '@/shared/office/host'
import * as word from '@/shared/office/word'
import * as excel from '@/shared/office/excel'
import * as powerpoint from '@/shared/office/powerpoint'
import { inlinePlainText, markdownToHtml } from '@/shared/office/markdown'

/**
 * Office tools run locally against the active document via Office.js. They serve
 * two paths that share these handlers:
 *  1. ToolShelf — the user picks a tool in the composer; `ChatWindow.onToolSelected`
 *     calls `runtime.tools.invoke(name)`.
 *  2. Server-driven — the `office` agent in ramp-ai-service declares matching
 *     (echo) tools; when the model calls one, the client tool renderer applies it
 *     by invoking the same local handler.
 *
 * Tool NAMES must match the server tool titles exactly and stay [a-zA-Z0-9_-]
 * (LLM tool-name constraint) — hence camelCase, no dots.
 */

const asString = (input: Record<string, unknown>, key = 'text'): string => {
  const value = input?.[key]
  return typeof value === 'string' ? value : ''
}

const asIndex = (input: Record<string, unknown>, key: string): number => {
  const value = Number(input?.[key])
  return Number.isInteger(value) ? value : -1
}

/** Word write payloads are Markdown; convert for Office.js insertHtml. */
const asHtml = (input: Record<string, unknown>, key = 'text'): string => markdownToHtml(asString(input, key))

const wordTools: AgentToolDefinition[] = [
  {
    name: 'getWordDocument',
    label: 'Read document',
    description: 'Read the full text of the Word document.',
    icon: '📄',
    handler: async () => ({ text: await word.getDocumentText() })
  },
  {
    name: 'getWordSelection',
    label: 'Read selection',
    description: 'Read the currently selected text in Word.',
    icon: '🔎',
    handler: async () => ({ text: await word.getSelection() })
  },
  {
    name: 'insertWordText',
    label: 'Insert at selection',
    description: 'Insert content (Markdown supported) at the end of the current Word selection.',
    icon: '➕',
    handler: async (input) => {
      await word.insertHtmlAtSelection(asHtml(input), asString(input, 'expectedSelectionText') || undefined)
      return { inserted: true }
    }
  },
  {
    name: 'appendWordText',
    label: 'Append to document',
    description: 'Append content (Markdown supported) to the end of the Word document.',
    icon: '➕',
    handler: async (input) => {
      await word.appendHtmlToDocument(asHtml(input))
      return { inserted: true }
    }
  },
  {
    name: 'replaceWordSelection',
    label: 'Replace selection',
    description: 'Replace the current Word selection with content (Markdown supported).',
    icon: '✏️',
    handler: async (input) => {
      // Plain (markup-free) replacements keep the selection's formatting.
      await word.replaceSelectionWithHtml(
        asHtml(input),
        asString(input, 'expectedSelectionText') || undefined,
        inlinePlainText(asString(input))
      )
      return { replaced: true }
    }
  },
  {
    name: 'replaceWordText',
    label: 'Replace text',
    description: 'Replace a span of existing document text, anchored by exact snippets.',
    icon: '🔁',
    handler: async (input) => {
      const findEnd = asString(input, 'findEnd')
      const paragraphIndex = asIndex(input, 'paragraphIndex')
      const occurrence = asIndex(input, 'occurrence')
      const findEndOccurrence = asIndex(input, 'findEndOccurrence')
      // Plain (markup-free) replacements keep the span's formatting.
      await word.replaceTextRangeWithHtml(
        asString(input, 'find'),
        asHtml(input),
        findEnd || undefined,
        inlinePlainText(asString(input)),
        paragraphIndex >= 0 ? paragraphIndex : undefined,
        asString(input, 'expectedParagraphText') || undefined,
        occurrence >= 0 ? occurrence : 0,
        findEndOccurrence >= 0 ? findEndOccurrence : 0
      )
      return { replaced: true }
    }
  },
  {
    name: 'replaceWordParagraphs',
    label: 'Replace paragraphs',
    description: 'Replace a range of paragraphs by their zero-based indexes.',
    icon: '📑',
    handler: async (input) => {
      await word.replaceParagraphRange(
        asIndex(input, 'start'),
        asIndex(input, 'end'),
        asHtml(input),
        asString(input, 'expectedStartText') || undefined,
        asString(input, 'expectedEndText') || undefined,
        inlinePlainText(asString(input))
      )
      return { replaced: true }
    }
  },
  {
    name: 'replaceWordDocument',
    label: 'Replace document',
    description: 'Replace the entire Word document body with new content (Markdown supported).',
    icon: '📝',
    handler: async (input) => {
      await word.replaceDocumentBodyWithHtml(asHtml(input))
      return { replaced: true }
    }
  }
]

const excelTools: AgentToolDefinition[] = [
  {
    name: 'getExcelSelection',
    label: 'Read selected range',
    description: 'Read the address and values of the selected Excel range.',
    icon: '🔎',
    handler: async () => ({ ...(await excel.getSelectedRange()) })
  },
  {
    name: 'readExcelRange',
    label: 'Read range',
    description: 'Read values from an A1-style range on the active worksheet.',
    icon: '📊',
    handler: async (input) => ({ ...(await excel.readRange(asString(input, 'address'))) })
  },
  {
    name: 'writeExcelRange',
    label: 'Write range',
    description: 'Write a 2D array of values starting at an A1-style address.',
    icon: '✏️',
    handler: async (input) => {
      const address = asString(input, 'address') || 'A1'
      const values = (Array.isArray(input?.values) ? input.values : [[asString(input)]]) as unknown[][]
      return { address: await excel.writeRange(address, values) }
    }
  }
]

const powerpointTools: AgentToolDefinition[] = [
  {
    name: 'getPowerPointSelection',
    label: 'Read selection',
    description: 'Read the selected text on the active slide.',
    icon: '🔎',
    handler: async () => ({ text: await powerpoint.getSelectedSlideText() })
  },
  {
    name: 'insertPowerPointText',
    label: 'Insert text box',
    description: 'Insert a text box with the supplied text on the active slide.',
    icon: '➕',
    handler: async (input) => {
      await powerpoint.insertTextBox(asString(input))
      return { inserted: true }
    }
  }
]

/** Map one batch edit from the model's schema to the Word batch shape (with
 * the same Markdown→HTML / plain-text classification as the single tools). */
function toWordBatchEdit(edit: Record<string, unknown>): word.WordBatchEdit {
  const kind = asString(edit, 'kind')
  const text = asString(edit)
  if (kind === 'replaceText') {
    return {
      kind: 'replaceText',
      find: asString(edit, 'find'),
      findEnd: asString(edit, 'findEnd') || undefined,
      paragraphIndex: asIndex(edit, 'paragraphIndex'),
      expectedParagraphText: asString(edit, 'expectedParagraphText'),
      occurrence: Math.max(0, asIndex(edit, 'occurrence')),
      findEndOccurrence: Math.max(0, asIndex(edit, 'findEndOccurrence')),
      html: markdownToHtml(text),
      plainText: inlinePlainText(text)
    }
  }
  if (kind === 'replaceParagraphs') {
    return {
      kind: 'replaceParagraphs',
      start: asIndex(edit, 'start'),
      end: asIndex(edit, 'end'),
      expectedStartText: asString(edit, 'expectedStartText'),
      expectedEndText: asString(edit, 'expectedEndText'),
      html: markdownToHtml(text),
      plainText: inlinePlainText(text)
    }
  }
  if (kind === 'insertBefore') {
    return {
      kind: 'insertBefore',
      paragraphIndex: asIndex(edit, 'paragraphIndex'),
      expectedParagraphText: asString(edit, 'expectedParagraphText'),
      html: markdownToHtml(text)
    }
  }
  return { kind: 'append', html: markdownToHtml(text) }
}

function asBatchEdits(input: Record<string, unknown>): word.WordBatchEdit[] {
  const edits = Array.isArray(input?.edits) ? (input.edits as Record<string, unknown>[]) : []
  return edits.map(toWordBatchEdit)
}

/**
 * Handlers reachable from proposal cards (applyOfficeToolLocally) but NOT
 * offered on the ToolShelf — the batch tool only makes sense as a model
 * proposal, never as a manual composer action.
 */
const cardOnlyWordTools: AgentToolDefinition[] = [
  {
    name: 'applyWordEdits',
    label: 'Apply edits',
    description: 'Apply a validated batch of Word edits in one transaction.',
    icon: '🗂️',
    handler: async (input) => {
      const result = await word.applyEditBatch(asBatchEdits(input), input.asTrackedChanges !== false)
      return { replaced: true, applied: result.applied, tracked: result.tracked }
    }
  }
]

const TOOLS_BY_HOST: Record<OfficeHost, AgentToolDefinition[]> = {
  word: wordTools,
  excel: excelTools,
  powerpoint: powerpointTools,
  unknown: []
}

export function officeToolsForHost(host: OfficeHost): AgentToolDefinition[] {
  return TOOLS_BY_HOST[host] ?? []
}

/**
 * Tool names that write back to the document. Used by:
 *  - the ToolShelf path, to default a write tool's input to the latest reply, and
 *  - the tool renderers, which render an "apply" card for these server tool calls.
 */
export const WRITE_TOOL_NAMES = new Set([
  'insertWordText',
  'appendWordText',
  'replaceWordSelection',
  'replaceWordText',
  'replaceWordParagraphs',
  'replaceWordDocument',
  'applyWordEdits',
  'writeExcelRange',
  'insertPowerPointText'
])

/**
 * Write tools whose proposal target can be located in the live document
 * *before* applying (by its anchor text / paragraph indexes / selection).
 */
const LOCATABLE_TARGET_TOOLS = new Set([
  'insertWordText',
  'replaceWordText',
  'replaceWordParagraphs',
  'replaceWordSelection',
  'appendWordText',
  'applyWordEdits'
])

/**
 * Word write tools that carry Markdown `text`. Once applied, that content lives
 * in the document, so we locate it by searching the inserted text instead of
 * the (now-gone) anchor.
 */
const WORD_CONTENT_TOOLS = new Set([
  'insertWordText',
  'appendWordText',
  'replaceWordSelection',
  'replaceWordText',
  'replaceWordParagraphs',
  'replaceWordDocument',
  'applyWordEdits'
])

/** The batch card locates via its first edit; applied, via that edit's content. */
function firstBatchEdit(input: Record<string, unknown>): Record<string, unknown> | undefined {
  const edits = Array.isArray(input?.edits) ? (input.edits as Record<string, unknown>[]) : []
  return edits[0]
}

export function canScrollToToolTarget(name: string, applied: boolean): boolean {
  return applied ? WORD_CONTENT_TOOLS.has(name) : LOCATABLE_TARGET_TOOLS.has(name)
}

/**
 * Scroll the document to a proposal and select it, so the user can see it in
 * context — without modifying anything. Before apply, reveals the target the
 * change will land on; after apply, reveals the inserted content itself.
 */
export async function scrollToOfficeToolTarget(
  name: string,
  input: Record<string, unknown>,
  applied: boolean
): Promise<void> {
  if (applied) {
    if (!WORD_CONTENT_TOOLS.has(name)) return
    const content = name === 'applyWordEdits' ? asString(firstBatchEdit(input) ?? {}) : asString(input)
    const found = await word.scrollToContent(content)
    if (!found) {
      throw new Error('Could not locate the applied change — the document may have changed since.')
    }
    return
  }
  if (name === 'applyWordEdits') {
    const first = firstBatchEdit(input)
    if (!first) return
    const kind = asString(first, 'kind')
    if (kind === 'append') return word.scrollToDocumentEnd()
    if (kind === 'replaceParagraphs') {
      const expectedStart = asString(first, 'expectedStartText')
      if (expectedStart && (await word.scrollToContent(expectedStart))) return
      return word.scrollToParagraphRange(asIndex(first, 'start'), asIndex(first, 'end'))
    }
    if (kind === 'insertBefore') {
      const expected = asString(first, 'expectedParagraphText')
      if (expected && (await word.scrollToContent(expected))) return
      return word.scrollToParagraphRange(asIndex(first, 'paragraphIndex'), asIndex(first, 'paragraphIndex'))
    }
    return word.scrollToTextRange(
      asString(first, 'find'),
      asString(first, 'findEnd') || undefined,
      asIndex(first, 'paragraphIndex') >= 0 ? asIndex(first, 'paragraphIndex') : undefined,
      asString(first, 'expectedParagraphText') || undefined,
      Math.max(0, asIndex(first, 'occurrence')),
      Math.max(0, asIndex(first, 'findEndOccurrence'))
    )
  }
  switch (name) {
    case 'insertWordText':
      return word.scrollToSelection()
    case 'replaceWordText':
      return word.scrollToTextRange(
        asString(input, 'find'),
        asString(input, 'findEnd') || undefined,
        asIndex(input, 'paragraphIndex') >= 0 ? asIndex(input, 'paragraphIndex') : undefined,
        asString(input, 'expectedParagraphText') || undefined,
        asIndex(input, 'occurrence') >= 0 ? asIndex(input, 'occurrence') : 0,
        asIndex(input, 'findEndOccurrence') >= 0 ? asIndex(input, 'findEndOccurrence') : 0
      )
    case 'replaceWordParagraphs': {
      // Prefer the expected paragraph TEXT over the raw index: paragraph
      // numbering drifts as the document is edited, so the index can point at a
      // different paragraph than the one the proposal targets. Anchoring on the
      // expected text lands on the real target (and matches what apply checks).
      const expectedStart = asString(input, 'expectedStartText')
      if (expectedStart && (await word.scrollToContent(expectedStart))) return
      return word.scrollToParagraphRange(asIndex(input, 'start'), asIndex(input, 'end'))
    }
    case 'replaceWordSelection':
      return word.scrollToSelection()
    case 'appendWordText':
      return word.scrollToDocumentEnd()
  }
}

/**
 * Result of checking whether a proposal can still be applied to the live
 * document:
 *  - `ok`          — the anchor still resolves; show Apply.
 *  - `stale`       — the document changed and the anchor no longer matches; the
 *                    renderer hides Apply instead of letting the user hit an error.
 *  - `unsafe`      — the range crosses table-cell paragraph boundaries and is
 *                    intentionally blocked to preserve document structure.
 *  - `ambiguous`   — an old/unscoped text anchor matches multiple locations.
 *  - `unsupported` — this tool has no anchor to validate (it targets the live
 *                    selection / whole document, or isn't a Word write), so it is
 *                    always actionable and the renderer leaves Apply in place.
 */
export type ApplyStatus = 'ok' | 'stale' | 'unsafe' | 'ambiguous' | 'unsupported'

/**
 * Read-only check, mirroring the apply path's anchoring, for whether a proposed
 * write still resolves against the current document. Lets the UI drop a stale
 * "Apply" button BEFORE the user clicks (the apply itself fails safely either
 * way). Unexpected Office.js failures surface as `unsupported` so a transient
 * glitch never hides a still-valid proposal.
 */
export async function canApplyOfficeTool(name: string, input: Record<string, unknown>): Promise<ApplyStatus> {
  try {
    switch (name) {
      case 'replaceWordText': {
        const find = asString(input, 'find')
        if (!find) return 'unsupported'
        const paragraphIndex = asIndex(input, 'paragraphIndex')
        return (await word.canResolveTextRange(
          find,
          asString(input, 'findEnd') || undefined,
          paragraphIndex >= 0 ? paragraphIndex : undefined,
          asString(input, 'expectedParagraphText') || undefined,
          asIndex(input, 'occurrence') >= 0 ? asIndex(input, 'occurrence') : 0,
          asIndex(input, 'findEndOccurrence') >= 0 ? asIndex(input, 'findEndOccurrence') : 0
        ))
          ? 'ok'
          : 'stale'
      }
      case 'replaceWordParagraphs':
        return (await word.canResolveParagraphRange(
          asIndex(input, 'start'),
          asIndex(input, 'end'),
          asString(input, 'expectedStartText') || undefined,
          asString(input, 'expectedEndText') || undefined
        ))
          ? 'ok'
          : 'stale'
      case 'insertWordText': {
        const expected = asString(input, 'expectedSelectionText')
        if (!expected) return 'unsupported'
        return (await word.canResolveSelection(expected, false)) ? 'ok' : 'stale'
      }
      case 'replaceWordSelection': {
        const expected = asString(input, 'expectedSelectionText')
        // Without an expectation the tool targets the live selection at click
        // time, so there is nothing to go stale.
        if (!expected) return 'unsupported'
        return (await word.canResolveSelection(expected)) ? 'ok' : 'stale'
      }
      case 'applyWordEdits': {
        const edits = asBatchEdits(input)
        if (edits.length === 0) return 'unsupported'
        // Every anchor must still resolve; one stale edit makes the whole
        // all-or-nothing batch stale.
        return (await word.canResolveEditBatch(edits)) ? 'ok' : 'stale'
      }
      // insert/append/replace-document and non-Word tools have no anchor to
      // invalidate — they always apply to the current document state.
      default:
        return 'unsupported'
    }
  } catch (error) {
    if (error instanceof word.UnsafeWordRangeError) return 'unsafe'
    if (error instanceof word.AmbiguousWordAnchorError) return 'ambiguous'
    return 'unsupported'
  }
}

/**
 * Subscribe to live document edits so a proposal can re-check its `ApplyStatus`.
 * Only Word exposes the change events we anchor on; for other hosts this is a
 * no-op and the returned unsubscribe does nothing.
 */
export function onOfficeDocumentChanged(host: OfficeHost, listener: () => void): () => void {
  if (host !== 'word') return () => {}
  return word.onDocumentChanged(listener)
}

/**
 * Apply a tool's document effect directly, WITHOUT going through
 * `runtime.tools.invoke` — invoke records a brand-new tool call in the chat,
 * which the renderer registry would draw as a second "Proposed change" card.
 */
export async function applyOfficeToolLocally(
  name: string,
  input: Record<string, unknown>
): Promise<Record<string, unknown> | void> {
  const tool = [...Object.values(TOOLS_BY_HOST).flat(), ...cardOnlyWordTools].find(
    (definition) => definition.name === name
  )
  if (!tool?.handler) throw new Error(`No local Office handler for tool "${name}"`)
  return tool.handler(input, { chatId: '', appendSystemMessage: () => {} })
}
